import { ConfirmationModal, ErrorPage, IconButton, InfoCardInner, Link, LoadingPage, Switch, TConfirmationModalProps, TInfoCardSectionColumnProps, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import { addDays, isAfter } from 'date-fns';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { useFilter } from '../../contexts/filters';
import { useQueryListOneCompany } from '../../entities/companies/queries';
import { getReadableWorkAnniversaryVariation } from '../../entities/companies/shared';
import { TListOneCompanyFieldsFragment, TWorkAnniversaryGiftVariations, useSoftDeleteCompanyMutation } from '../../generated/graphql';
import { EDIT_PATH } from '../../routes/paths';
import styles from './CompanyDetails.module.scss';
import { CompanyModal } from './CompanyModal';

export const CompanyDetails = () => {
  const { filtersModalMode } = useFilter();
  useEffect(() => {
    filtersModalMode();
  }, []); // eslint-disable-line

  const navigate = useNavigate();
  const { companyId = '0' } = useParams();

  const [deletedAllLoading, setDeletedAllLoading] = useState<boolean>(false);
  const [deleteConfirmationProps, setDeleteConfirmationProps] = useState<Omit<TConfirmationModalProps, 'closeModal' | 'confirmLoading' | 'onCancelClick'>>();

  const {
    data: company,
    loading,
    error
  } = useQueryListOneCompany({
    id: companyId
  });

  const [softDeleteCompany] = useSoftDeleteCompanyMutation();

  const giftConfigurationColumns = useMemo(() => {
    const cols: TInfoCardSectionColumnProps<TListOneCompanyFieldsFragment>[] = [
      {
        label: 'Employee Birthday',
        render: (data) => {
          return <Switch name='employeeBirthdayActivated' readOnly={true} value={data?.employeeBirthdayActivated} />;
        }
      },
      {
        label: 'Employee Work Anniversary',
        render: (data) => {
          return <Switch name='employeeWorkAnniversaryActivated' readOnly={true} value={data?.employeeWorkAnniversaryActivated} />;
        }
      },
      {
        label: 'Employee New Hire Kit',
        render: (data) => {
          return <Switch name='employeeNewHireKitActivated' readOnly={true} value={data?.employeeNewHireKitActivated} />;
        }
      },
      {
        label: 'Significant Other Birthday',
        render: (data) => {
          return <Switch name='significantOtherBirthdayActivated' readOnly={true} value={data?.significantOtherBirthdayActivated} />;
        }
      },
      {
        label: 'Child Birthday',
        render: (data) => {
          return <Switch name='childBirthdayActivated' readOnly={true} value={data?.childBirthdayActivated} />;
        }
      },
      {
        label: 'Pet Birthday',
        render: (data) => {
          return <Switch name='petBirthdayActivated' readOnly={true} value={data?.petBirthdayActivated} />;
        }
      }
    ];

    if (company?.employeeNewHireKitActivated)
      cols.push({
        label: 'Custom Onboarding Brick',
        render: (data) => {
          return <Switch name='hasCustomOnboardBrick' readOnly={true} value={data?.hasCustomOnboardBrick || undefined} />;
        }
      });

    if (company?.employeeWorkAnniversaryActivated)
      cols.push({
        label: 'Work Anniversary Variation',
        render: (data) => {
          return getReadableWorkAnniversaryVariation(data?.workAnniversaryGiftVariation as TWorkAnniversaryGiftVariations);
        }
      });

    if (company?.specialNote)
      cols.push({
        label: 'Gift Special Notes',
        render: (data) => {
          return data?.specialNote;
        }
      });

    if (company?.swag)
      cols.push({
        label: 'New Hire Notes',
        render: (data) => {
          return data?.swag;
        }
      });

    if (company?.employeeWorkAnniversaryActivated && company?.workAnniversaryGiftVariation === TWorkAnniversaryGiftVariations.Custom)
      cols.push({
        label: 'Custom Work Anniversary Notes',
        render: (data) => data?.customWorkAnniversaryNotes
      });

    return cols;
  }, [company]);

  const goToEditCompany = useCallback(() => {
    navigate(generatePath(`../${EDIT_PATH}`, { companyId }));
  }, [navigate, companyId]);

  const onDeletedAllClick = useCallback(async () => {
    setDeletedAllLoading(true);

    try {
      await softDeleteCompany({
        variables: {
          id: companyId as string,
          _version: company?._version as number
        }
      });

      setDeleteConfirmationProps(undefined);
    } catch (error) {
      console.error(serializeError(error));
      toast.error('There was an error trying to deleted all shipments.');
    } finally {
      setDeletedAllLoading(false);
    }
  }, [companyId, company?._version, softDeleteCompany]);

  const onDeleteClick = useCallback(() => {
    setDeleteConfirmationProps({
      headline: 'Remove Company',
      supportingText: `You are going to remove ${company?.name}. Are you sure?`,
      confirmLabel: 'Remove',
      onConfirmClick: () => onDeletedAllClick()
    });
  }, [onDeletedAllClick, setDeleteConfirmationProps, company]);

  const topComponent = useMemo(() => {
    if (company)
      return (
        <div className={styles.topComponent}>
          <div className={styles.topTitleContainer}>
            <h2 className={styles.topTitle}>{company.name}</h2>
            <div className={styles.topIcons}>
              <Tooltip message='Edit Company'>
                <IconButton disabled={!company.fullyConfigured} icon='edit' onClick={goToEditCompany} variant='outlined' />
                {company.endDate && company.isLive && company.active && isAfter(addDays(new Date(), 90), new Date(company.endDate)) && <IconButton icon='delete' onClick={onDeleteClick} variant='filled' />}
              </Tooltip>
            </div>
          </div>
        </div>
      );
  }, [company, goToEditCompany, onDeleteClick]);

  if (loading)
    return (
      <CompanyModal headline='Company Details'>
        <LoadingPage />
      </CompanyModal>
    );

  if (error) {
    console.error('query Apollo ERROR:', error);
    toast.error('An error has ocurred, check the console for details. (query Apollo ERROR)');
    //if (error.message) toast.error(error.message);
    if (!company)
      return (
        <CompanyModal headline='Company Details'>
          <ErrorPage error={error} />
        </CompanyModal>
      );
  }

  return (
    <>
      {!_.isEmpty(deleteConfirmationProps) && (
        <ConfirmationModal {...deleteConfirmationProps} closeModal={() => setDeleteConfirmationProps(undefined)} confirmLoading={deletedAllLoading} onCancelClick={() => setDeleteConfirmationProps(undefined)} />
      )}
      <CompanyModal headline='Company Details'>
        <InfoCardInner<TListOneCompanyFieldsFragment>
          data={company}
          sections={[
            {
              topComponent: () => topComponent,
              columns: [
                {
                  label: 'Chocolate Soup Manager',
                  accessor: 'accountManager'
                },
                {
                  label: 'Delivery Method',
                  accessor: 'deliveryMethod'
                },
                {
                  label: 'Subdomain',
                  accessor: 'subdomain'
                },
                {
                  label: 'Full Domain',
                  render: (data) => {
                    if (company?.fullyConfigured) {
                      const url = `https://${data?.subdomain}.${import.meta.env.REACT_APP_BASE_DOMAIN || 'chocolatesoup.ca'}`;

                      return <Link href={url} label={url} />;
                    } else return <span>Infrastructure configuring...</span>;
                  }
                },
                {
                  label: 'Email Domains',
                  render: (data) => {
                    return (
                      <div className={styles.emailDomains}>
                        {data?.emailDomains?.map((emailDomain) => {
                          return <span key={emailDomain}>{emailDomain}</span>;
                        })}
                      </div>
                    );
                  }
                },
                {
                  label: 'Google Login Enabled',
                  render: (data) => {
                    return data?.googleLoginEnabled ? 'Enabled' : 'Disabled';
                  }
                },
                {
                  label: 'Lego Color',
                  accessor: 'legoColor'
                },
                {
                  label: 'Tissue Color',
                  accessor: 'tissueColor'
                }
              ]
            },
            {
              title: 'Gift Configuration',
              columns: giftConfigurationColumns
            },
            {
              title: 'Contact Information',
              columns: [
                { label: 'Contact Name', render: () => company?.contact?.name },
                { label: 'Contact Email', render: () => company?.contact?.email },
                { label: 'Contact Phone Number', render: () => company?.contact?.phoneNumber }
              ]
            },
            ...(company?.address
              ? [
                  {
                    title: 'Address',
                    columns: [
                      {
                        label: 'Address 1',
                        render: () => company.address?.address1
                      },
                      {
                        label: 'Address 2',
                        render: () => company.address?.address2
                      },
                      {
                        label: 'City',
                        render: () => company.address?.city
                      },
                      {
                        label: 'State',
                        render: () => company.address?.state
                      },
                      {
                        label: 'Country',
                        render: () => company.address?.country
                      },
                      {
                        label: 'Postal Code',
                        render: () => company.address?.zipCode
                      }
                    ]
                  }
                ]
              : []),
            {
              title: 'Billing',
              columns: [
                {
                  label: 'Subscription Price per User',
                  accessor: 'subscriptionPricePerUser'
                },
                {
                  label: 'Subscription Price Currency',
                  accessor: 'subscriptionPriceCurrency'
                }
              ]
            }
          ]}
        />
      </CompanyModal>
    </>
  );
};
